/**
 * Layout con estructura del CatcSoft Design System (línea A "Datadog refined").
 *
 * Estructura DS:
 *   .sar-app > .sar-side (brand+workspace+nav+foot user)
 *            > .sar-main > .sar-top (toggle + crumbs + search + theme + bell + user)
 *                        > .sar-content > <Outlet/>
 *
 * Responsive: en mobile, sidebar pasa a drawer overlay (no cubierto por el DS).
 * El theme (light/dark) lo controla ThemeContext; data-direction="a" y
 * data-theme se ponen en <html> desde ThemeContext.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { globalSearch, type SearchResults } from "../api/search";
import { useAuth } from "../auth/useAuth";
import { useWorkspace } from "../workspace/WorkspaceContext";
import { useExecutionContext } from "../workspace/ExecutionContext";
import { useTheme } from "../theme/ThemeContext";
import logoUrl from "../styles/catcsoft/assets/logo.svg";
import {
  IconGauge,
  IconFolder,
  IconBox,
  IconShield,
  IconReports,
  IconClock,
  IconLayers,
  IconCpu,
  IconUsers,
  IconChevD,
  IconChevR,
  IconSearch,
  IconSun,
  IconMoon,
  IconUser,
  IconLogout,
} from "./icons";
import { NotificationsBell } from "./NotificationsBell";
import { BackgroundDock } from "./BackgroundDock";

interface NavItem {
  to: string;
  label: string;
  icon: () => ReactNode;
  rolesRequired?: string[];
  /** Alternativa a rolesRequired: la opción se muestra si el usuario tiene
   *  ALGUNO de estos permisos (gestión de roles — el rol controla la opción). */
  permissionsRequired?: string[];
}

// Navegación agrupada POR CAPACIDAD (no plano). Cada grupo se filtra por rol;
// los grupos vacíos para el rol no se renderizan.
const _DEV_ROLES = ["ADMIN", "ARQUITECTO", "FRONTEND", "QA", "DEVELOPER", "GATEKEEPER"];

interface NavGroup {
  title: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: "General",
    items: [{ to: "/dashboard", label: "Dashboard", icon: IconGauge }],
  },
  // Menú separado POR CONTEXTO (orden Carlos 2026-07-14): lo del USUARIO
  // (su trabajo diario) aparte de lo de GESTIÓN (gobierno del equipo).
  // "Calidad por Dev" RETIRADA (centralizada en Consistencia); endpoints intactos.
  {
    title: "SDD · Sprints",
    items: [
      { to: "/projects", label: "Sprints", icon: IconFolder },
    ],
  },
  {
    // Línea de PLANEACIÓN (0044): contexto separado de la fábrica SDD —
    // backlog de consultoría + documentos HU + cruce solo-lectura con sprints.
    title: "Planeación",
    items: [
      // Gobernado por PERMISO (no rol hardcodeado): se activa por rol desde
      // la pantalla Roles. De arranque solo ARQUITECTO tiene plan.view.
      { to: "/plan", label: "Proyectos", icon: IconLayers, permissionsRequired: ["plan.view"] },
    ],
  },
  {
    title: "Mi trabajo",
    items: [
      { to: "/avances", label: "Avances", icon: IconReports, rolesRequired: _DEV_ROLES },
      { to: "/diagnosis", label: "Diagnóstico", icon: IconReports, rolesRequired: _DEV_ROLES },
      { to: "/my-certifications", label: "Mis certificaciones", icon: IconShield, rolesRequired: _DEV_ROLES },
      { to: "/my-consistency", label: "Mi consistencia", icon: IconShield },
    ],
  },
  {
    title: "Gestión",
    items: [
      { to: "/certifications", label: "Certificaciones", icon: IconShield, rolesRequired: ["ADMIN", "ARQUITECTO", "GATEKEEPER"] },
      { to: "/dev-consistency", label: "Consistencia (equipo)", icon: IconShield, rolesRequired: ["ADMIN", "ARQUITECTO", "GERENCIA"] },
      // Barrido de patrón por servicio completo: foto del arquitecto, no del dev.
      { to: "/service-diagnosis", label: "Diagnóstico de servicio", icon: IconReports, rolesRequired: ["ADMIN", "ARQUITECTO"] },
      // Consolidado del proyecto: el panorama que el detalle por servicio no da.
      // GERENCIA entra por la pestaña "Por sprint": estado del ecosistema, sin
      // atribución a personas. Las otras pestañas se le ocultan en la página.
      { to: "/service-panorama", label: "Panorama de servicios", icon: IconReports, rolesRequired: ["ADMIN", "ARQUITECTO", "GERENCIA"] },
      // Metrica de gerencia: score ponderado por servicio, foto nocturna por workspace. La
      // unidad es el SERVICIO (no la HT): en develop dos HTs que tocan el mismo
      // paquete comparten sus hallazgos y no hay forma de atribuirlos.
      { to: "/service-metrics", label: "Calidad por servicio", icon: IconReports, rolesRequired: ["ADMIN", "ARQUITECTO", "GERENCIA"] },
    ],
  },
  // Grupo "Auditoría runtime" RETIRADO de la UI (Carlos 2026-07-13): Aprobaciones
  // (0 decisiones históricas; el aval humano vive en el flujo PR/gatekeeper) y
  // Reports (dashboards sobre validation_runs, muertos desde mayo; superado por
  // Calidad por Dev / Tablero del sprint / Avances). Endpoints backend intactos.
  {
    title: "Infraestructura",
    items: [
      { to: "/services", label: "Services", icon: IconCpu },
      { to: "/environments", label: "Environments", icon: IconBox, rolesRequired: ["ADMIN", "ARQUITECTO", "GERENCIA"] },
    ],
  },
  {
    title: "Operación",
    items: [
      { to: "/scheduled-jobs", label: "Scheduled", icon: IconClock, rolesRequired: ["ADMIN", "ARQUITECTO"] },
    ],
  },
];

const NAV_ADMIN: NavItem[] = [
  {
    to: "/workspaces",
    label: "Workspaces",
    icon: IconLayers,
    rolesRequired: ["ADMIN"],
  },
  {
    // Gestión visible de roles: matriz rol × permiso con toggles — activa
    // módulos por rol (ej. Planeación) sin tocar código.
    to: "/roles",
    label: "Roles",
    icon: IconShield,
    rolesRequired: ["ADMIN"],
  },
  {
    to: "/ai-config",
    label: "Config IA",
    icon: IconCpu,
    rolesRequired: ["ADMIN", "ARQUITECTO", "GERENCIA"],
  },
  {
    to: "/activity",
    label: "Actividad",
    icon: IconClock,
    rolesRequired: ["ADMIN", "ARQUITECTO", "GERENCIA"],
  },
  {
    to: "/users",
    label: "Usuarios",
    icon: IconUsers,
    rolesRequired: ["ADMIN"],
  },
  {
    to: "/rules",
    label: "Reglas",
    icon: IconShield,
    rolesRequired: ["ADMIN", "ARQUITECTO"],
  },
  {
    to: "/canonical-patterns",
    label: "Patrones",
    icon: IconCpu,
    rolesRequired: ["ADMIN", "ARQUITECTO"],
  },
];

function filterByAccess(
  items: NavItem[],
  roles: string[] | undefined,
  permissions: string[] | undefined,
): NavItem[] {
  return items.filter((item) => {
    if (item.permissionsRequired && item.permissionsRequired.length > 0) {
      if (!permissions) return false;
      return item.permissionsRequired.some((p) => permissions.includes(p));
    }
    if (!item.rolesRequired || item.rolesRequired.length === 0) return true;
    if (!roles) return false;
    return item.rolesRequired.some((r) => roles.includes(r));
  });
}

function initialsFrom(name?: string | null, fallback?: string): string {
  const base = (name || fallback || "?").trim();
  if (!base) return "?";
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function buildCrumbs(pathname: string): string[] {
  const segs = pathname.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
  if (segs.length === 0) return ["Dashboard"];
  return segs.map((s) => {
    // UUIDs: no partir por guiones, mostrar solo los primeros 8 chars.
    if (UUID_RE.test(s)) return s.slice(0, 8) + "…";
    // Numéricos puros (htId, etc): tal cual.
    if (/^\d+$/.test(s)) return `#${s}`;
    return s.replace(/-/g, " ").replace(/^\w/, (c) => c.toUpperCase());
  });
}


/**
 * Eje de PROYECTO DE PLANEACIÓN — raíz del contexto de ejecución.
 *
 * Carlos 2026-08-09: nada se lanza sin proyecto, environment y workspace. Por eso
 * el eje vive en el sidebar y no dentro de cada pantalla: el contexto es de la
 * aplicación, no de la vista, y dos pantallas en contextos distintos a la vez es
 * justo lo que produce mediciones incomparables.
 *
 * Con UN proyecto se muestra fijo y bloqueado; con varios, desplegable. La
 * preselección viene resuelta del servidor.
 */
function SelectorProyecto() {
  const {
    proyectos,
    planProject,
    setPlanProject,
    bloqueado,
    motivoSinContexto,
    workspace,
    isLoading,
  } = useExecutionContext();
  const [abierto, setAbierto] = useState(false);

  if (isLoading) return null;

  // Sin proyectos asignados no hay nada que elegir NI que lanzar: se dice, en vez
  // de mostrar un selector vacío que parece un fallo de carga.
  if (!proyectos.length) {
    return (
      <div className="sar-side-ws">
        <div className="sar-eyebrow" style={{ marginBottom: 6 }}>
          Proyecto
        </div>
        <div className="ws-card" style={{ cursor: "default", opacity: 0.7 }}>
          <span className="ws-dot" />
          <span className="ws-name">
            <b>Sin proyecto</b>
            <small>{motivoSinContexto ?? "Pide que te asignen a uno"}</small>
          </span>
        </div>
      </div>
    );
  }

  // El environment del workspace activo se muestra aquí y no en otra pantalla:
  // es contra lo que se va a medir, y hasta ahora no se veía en ninguna parte.
  const pie = workspace
    ? workspace.utilizable
      ? `${workspace.name} · ${workspace.environment_name ?? "sin env"}`
      : (workspace.motivo ?? "workspace no utilizable")
    : "sin workspace enlazado";

  return (
    <div className="sar-side-ws">
      <div className="sar-eyebrow" style={{ marginBottom: 6 }}>
        Proyecto
      </div>
      <div style={{ position: "relative" }}>
        <button
          type="button"
          className="ws-card"
          onClick={() => !bloqueado && setAbierto((v) => !v)}
          aria-haspopup={bloqueado ? undefined : "listbox"}
          aria-expanded={bloqueado ? undefined : abierto}
          disabled={bloqueado}
          title={
            bloqueado
              ? "Tienes un solo proyecto asignado"
              : "Cambiar de proyecto"
          }
          style={bloqueado ? { cursor: "default" } : undefined}
        >
          <span className="ws-dot" />
          <span className="ws-name">
            <b>{planProject?.name ?? "Selecciona…"}</b>
            <small
              style={
                workspace && !workspace.utilizable
                  ? { color: "var(--sar-warn, #d97706)" }
                  : undefined
              }
            >
              {pie}
            </small>
          </span>
        </button>
        {abierto && !bloqueado && (
          <div className="ws-menu" role="listbox">
            {proyectos.map((p) => (
              <button
                key={p.id}
                type="button"
                role="option"
                aria-selected={p.id === planProject?.id}
                className="ws-menu-item"
                onClick={() => {
                  setPlanProject(p.id);
                  setAbierto(false);
                }}
              >
                <span className="ws-name">
                  <b>{p.name}</b>
                  <small>
                    {p.code}
                    {p.status !== "active" ? ` · ${p.status}` : ""}
                  </small>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const {
    workspaces: todosLosWorkspaces,
    activeWorkspaceId,
    activeWorkspace,
    setActiveWorkspace,
  } = useWorkspace();
  // El workspace elegible es uno de los que EJECUTAN el proyecto activo, no
  // cualquiera que el usuario vea (Carlos 2026-08-09). Sin esta restricción se
  // podía tener el proyecto de un cliente y el workspace de otro seleccionados a
  // la vez, y el lanzamiento fallaba recién en el servidor sin decir por qué.
  const { workspaces: wsDelProyecto, planProjectId } = useExecutionContext();
  const workspaces = useMemo(() => {
    if (!planProjectId) return todosLosWorkspaces;
    const permitidos = new Set(wsDelProyecto.map((w) => w.id));
    return todosLosWorkspaces.filter((w) => permitidos.has(w.id));
  }, [todosLosWorkspaces, wsDelProyecto, planProjectId]);

  // Si el workspace activo no pertenece al proyecto, se corrige solo: dejarlo
  // seleccionado mostraría datos de un contexto y lanzaría contra otro.
  useEffect(() => {
    if (!workspaces.length) return;
    if (!activeWorkspaceId || !workspaces.some((w) => w.id === activeWorkspaceId)) {
      setActiveWorkspace(workspaces[0].id);
    }
  }, [workspaces, activeWorkspaceId, setActiveWorkspace]);
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  // Persiste el estado colapsado entre recargas.
  const [sideCollapsed, setSideCollapsed] = useState(
    () => localStorage.getItem("sar.sideCollapsed") === "1",
  );
  const [wsMenuOpen, setWsMenuOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
    setWsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem("sar.sideCollapsed", sideCollapsed ? "1" : "0");
  }, [sideCollapsed]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navGroups = NAV_GROUPS
    .map((g) => ({ title: g.title, items: filterByAccess(g.items, user?.roles, user?.permissions) }))
    .filter((g) => g.items.length > 0);
  const navAdmin = filterByAccess(NAV_ADMIN, user?.roles, user?.permissions);
  const showAdmin = navAdmin.length > 0;
  const crumbs = buildCrumbs(location.pathname);

  const sideContent = (
    <>
      <div className="sar-side-brand">
        <img
          src={logoUrl}
          alt=""
          width={28}
          height={28}
          style={{ display: "block", flexShrink: 0 }}
        />
        {!sideCollapsed && (
          <div className="brand-wordmark">
            <span className="brand-name">
              Simappe<span className="brand-soft"> API Runner</span>
            </span>
            <span className="brand-tag">CatcSoft Platform · v{__APP_VERSION__}</span>
          </div>
        )}
      </div>

      {/*
        Eje de PROYECTO (Carlos 2026-08-09). Va ARRIBA del de workspace porque es
        la raíz de la cadena: Proyecto de Planeación → Workspace → Environment →
        Sprint. Con un solo proyecto asignado se muestra fijo y bloqueado; con
        varios, desplegable arrancando en el primero activo — y esa preselección
        la resuelve el servidor, no esta pantalla.
      */}
      {!sideCollapsed && <SelectorProyecto />}

      {!sideCollapsed && (
        <div className="sar-side-ws">
          <div className="sar-eyebrow" style={{ marginBottom: 6 }}>
            Workspace
          </div>
          {workspaces.length === 0 ? (
            <div
              className="ws-card"
              style={{ cursor: "default", opacity: 0.7 }}
            >
              <span className="ws-dot" />
              <span className="ws-name">
                <b>Sin workspaces</b>
                <small>ADMIN crea uno en /workspaces</small>
              </span>
            </div>
          ) : (
            <div style={{ position: "relative" }}>
              <button
                type="button"
                className="ws-card"
                onClick={() => setWsMenuOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={wsMenuOpen}
              >
                <span className="ws-dot" />
                <span className="ws-name">
                  <b>{activeWorkspace?.name ?? "Selecciona…"}</b>
                  <small>{activeWorkspace?.code ?? ""}</small>
                </span>
                <IconChevD />
              </button>
              {wsMenuOpen && (
                <ul
                  role="listbox"
                  style={{
                    listStyle: "none",
                    margin: "4px 0 0",
                    padding: 4,
                    background: "var(--sar-surface)",
                    border: "1px solid var(--sar-line)",
                    borderRadius: 8,
                    boxShadow: "var(--cs-shadow)",
                    position: "absolute",
                    left: 0,
                    right: 0,
                    zIndex: 30,
                  }}
                >
                  {workspaces.map((w) => (
                    <li key={w.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveWorkspace(w.id);
                          setWsMenuOpen(false);
                        }}
                        style={{
                          display: "block",
                          width: "100%",
                          textAlign: "left",
                          padding: "6px 8px",
                          background:
                            w.id === activeWorkspaceId
                              ? "var(--sar-surface-2)"
                              : "transparent",
                          border: "0",
                          borderRadius: 6,
                          color: "var(--sar-text)",
                          font: "inherit",
                          cursor: "pointer",
                        }}
                      >
                        <b style={{ display: "block", fontSize: 13 }}>
                          {w.name}
                        </b>
                        <small style={{ color: "var(--sar-text-3)" }}>
                          {w.code}
                        </small>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {navGroups.map((group) => (
        <div key={group.title}>
          <div className="sar-side-section">{sideCollapsed ? "" : group.title}</div>
          <nav className="sar-nav">
            {group.items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => (isActive ? "active" : "")}
                  title={sideCollapsed ? item.label : undefined}
                  end={item.to === "/dashboard"}
                >
                  <Icon />
                  {!sideCollapsed && <span className="label">{item.label}</span>}
                </NavLink>
              );
            })}
          </nav>
        </div>
      ))}

      {showAdmin && (
        <>
          <div className="sar-side-section">
            {sideCollapsed ? "" : "Administración"}
          </div>
          <nav className="sar-nav">
            {navAdmin.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => (isActive ? "active" : "")}
                  title={sideCollapsed ? item.label : undefined}
                >
                  <Icon />
                  {!sideCollapsed && (
                    <span className="label">{item.label}</span>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </>
      )}

      <div className="sar-side-foot">
        <NavLink
          to="/me/profile"
          className="avatar"
          title="Mi perfil"
          style={{ textDecoration: "none" }}
        >
          {initialsFrom(user?.display_name, user?.username)}
        </NavLink>
        {!sideCollapsed && (
          <div className="who" style={{ flex: 1, minWidth: 0 }}>
            <b>{user?.display_name || user?.username || "—"}</b>
            <div className="email">{user?.email || ""}</div>
          </div>
        )}
        {!sideCollapsed && (
          <button
            type="button"
            className="iconbtn"
            title="Cerrar sesión"
            onClick={handleLogout}
            style={{
              width: 26,
              height: 26,
              padding: 0,
              border: "0",
              background: "transparent",
              color: "var(--sar-text-3)",
              cursor: "pointer",
            }}
          >
            <IconLogout />
          </button>
        )}
      </div>

      {!sideCollapsed && (
        <div className="sar-side-attr">
          <span>v{__APP_VERSION__}</span>
          <span>© CatcSoft</span>
        </div>
      )}
    </>
  );

  return (
    <div className="sar-app">
      <aside
        className="sar-side"
        data-collapsed={sideCollapsed ? "true" : "false"}
      >
        {sideContent}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-slate-900/50 md:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside
            className="sar-side fixed inset-y-0 left-0 z-50 md:hidden"
            data-collapsed="false"
            style={{ width: 280, maxWidth: "85vw" }}
          >
            {sideContent}
          </aside>
        </>
      )}

      <div className="sar-main">
        <header className="sar-top">
          <button
            type="button"
            className="iconbtn"
            aria-label="Toggle sidebar"
            onClick={() => {
              // En desktop colapsa; en mobile abre drawer.
              if (window.matchMedia("(min-width: 768px)").matches) {
                setSideCollapsed((v) => !v);
              } else {
                setMobileOpen(true);
              }
            }}
          >
            <IconLayers />
          </button>

          <div className="crumbs">
            {crumbs.map((c, i) => (
              <span
                key={i}
                className={i === crumbs.length - 1 ? "current" : undefined}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                {i > 0 && <IconChevR />}
                {c}
              </span>
            ))}
          </div>

          <div className="grow" />

          <GlobalSearch />

          <button
            type="button"
            className="iconbtn"
            title="Toggle theme"
            onClick={toggleTheme}
          >
            {theme === "dark" ? <IconSun /> : <IconMoon />}
          </button>
          {/* El indicador de background se fusionó con la campana (Carlos 2026-07-28):
              aquí quedan los resultados finales; lo que está corriendo vive en el
              dock inferior. */}
          <NotificationsBell />
          <NavLink
            to="/me/profile"
            className="iconbtn"
            title="Mi perfil"
          >
            <IconUser />
          </NavLink>
        </header>

        <div className="sar-content">
          <Outlet />
        </div>
      </div>

      {/* Procesamiento en background (barridos por servicio): panel inferior no
          bloqueante; se oculta solo cuando no hay nada en curso. */}
      <BackgroundDock />
    </div>
  );
}

// ===========================================================================
// Búsqueda global (⌘K) — Carlos 2026-07-13: el input era decorativo; ahora
// filtra SERVICES, SPRINTS y HTs visibles y navega al resultado.
// ===========================================================================

function GlobalSearch() {
  const navigate = useNavigate();
  const { activeWorkspaceId } = useWorkspace();
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<SearchResults | null>(null);
  const [open, setOpen] = useState(false);

  // ⌘K / Ctrl+K enfoca; Escape cierra.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Cierra al hacer clic fuera.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Debounce 250ms.
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults(null);
      return;
    }
    const t = setTimeout(() => {
      globalSearch(term, activeWorkspaceId)
        .then((r) => { setResults(r); setOpen(true); })
        .catch(() => setResults(null));
    }, 250);
    return () => clearTimeout(t);
  }, [q, activeWorkspaceId]);

  function go(path: string) {
    setOpen(false);
    setQ("");
    setResults(null);
    navigate(path);
  }

  const vacio = results && !results.sprints.length && !results.hts.length && !results.services.length;

  return (
    <div
      ref={wrapRef}
      className="search-wrap"
      style={{ flex: 1, maxWidth: 360, position: "relative" }}
    >
      <IconSearch />
      <input
        ref={inputRef}
        className="search"
        placeholder="Buscar services, sprints, HTs…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => { if (results) setOpen(true); }}
      />
      <kbd>⌘K</kbd>
      {open && results && (
        <div
          style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
            zIndex: 60, maxHeight: 380, overflowY: "auto",
            background: "var(--sar-surface, #fff)",
            border: "1px solid var(--sar-border, #e2e8f0)",
            borderRadius: 8, boxShadow: "0 8px 24px rgba(15,23,42,.12)",
            padding: 6, fontSize: 13,
          }}
        >
          {vacio && (
            <div style={{ padding: "8px 10px", color: "var(--sar-muted, #94a3b8)" }}>
              Sin resultados para “{results.q}”.
            </div>
          )}
          {results.services.length > 0 && <SearchGroup label="Services" />}
          {results.services.map((s) => (
            <SearchRow key={`s-${s.id}`} onClick={() => go(`/services/${s.id}`)}
              main={s.name} sub={s.code ?? undefined} />
          ))}
          {results.sprints.length > 0 && <SearchGroup label="Sprints" />}
          {results.sprints.map((p) => (
            <SearchRow key={`p-${p.id}`} onClick={() => go(`/projects/${p.id}`)}
              main={p.name} sub={p.code ?? undefined} />
          ))}
          {results.hts.length > 0 && <SearchGroup label="Hojas técnicas" />}
          {results.hts.map((h) => (
            <SearchRow key={`h-${h.id}`}
              onClick={() => go(`/projects/${h.project_id}/hts/${h.id}`)}
              main={h.ht_code} sub={h.project_name} />
          ))}
        </div>
      )}
    </div>
  );
}

function SearchGroup({ label }: { label: string }) {
  return (
    <div style={{
      padding: "6px 10px 2px", fontSize: 10, fontWeight: 700,
      textTransform: "uppercase", letterSpacing: ".06em",
      color: "var(--sar-muted, #94a3b8)",
    }}>
      {label}
    </div>
  );
}

function SearchRow({ main, sub, onClick }: { main: string; sub?: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex", width: "100%", alignItems: "baseline", gap: 8,
        padding: "7px 10px", borderRadius: 6, border: 0, background: "transparent",
        cursor: "pointer", textAlign: "left", color: "var(--sar-text, #0f172a)",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--sar-surface-2, #f1f5f9)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      <span style={{ fontWeight: 600 }}>{main}</span>
      {sub && <span style={{ fontSize: 11, color: "var(--sar-muted, #94a3b8)" }}>{sub}</span>}
    </button>
  );
}
